import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useBulkAssignCandidatesMulti, useVacancies } from '@/hooks/use-recruitment';
import { multiAssignNotice, singleAssignNotice } from '../lib/bulk-assign-notice';
import { extractErrorMessage } from '@/lib/errors';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

const MAX_BULK_CANDIDATES = 100;
const MAX_VACANCIES = 50;

export interface AssignableCandidate {
  id: string;
  fullName: string;
  activeAssignments: Array<{ vacancyId: string }>;
}

interface AssignCandidateDialogProps {
  /** Single-candidate mode: pass one candidate. Pre-checked rows reflect activeAssignments. */
  candidate?: AssignableCandidate | null;
  /** Bulk mode: pass selected candidate IDs (used when `candidate` is not provided). */
  candidateIds?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after successful assignment (e.g. parent clears selected Set). */
  onSuccess?: () => void;
}

export function AssignCandidateDialog({
  candidate,
  candidateIds: bulkIds,
  open,
  onOpenChange,
  onSuccess,
}: AssignCandidateDialogProps) {
  const isSingle = !!candidate;
  const candidateIds = useMemo(
    () => (isSingle ? [candidate!.id] : (bulkIds ?? [])),
    [isSingle, candidate, bulkIds]
  );
  const totalCandidates = candidateIds.length;

  const [search, setSearch] = useState('');
  const [selectedVacancyIds, setSelectedVacancyIds] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: vacanciesData, isLoading } = useVacancies({
    status: 'ACTIVE',
    limit: 50,
    search: debouncedSearch || undefined,
  });

  const vacancies = vacanciesData?.data ?? [];
  const multiMutation = useBulkAssignCandidatesMulti();

  const alreadyAssignedIds = useMemo(
    () => new Set(isSingle ? candidate!.activeAssignments.map((a) => a.vacancyId) : []),
    [isSingle, candidate]
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedVacancyIds(new Set());
      setError(null);
    }
  }, [open]);

  // Reset selected vacancies when the visible result set changes (after debounce).
  useEffect(() => {
    setSelectedVacancyIds(new Set());
  }, [debouncedSearch]);

  const overCandidateLimit = totalCandidates > MAX_BULK_CANDIDATES;
  const overVacancyLimit = selectedVacancyIds.size > MAX_VACANCIES;

  const toggleVacancy = (id: string) => {
    setSelectedVacancyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (
      selectedVacancyIds.size === 0 ||
      totalCandidates === 0 ||
      overCandidateLimit ||
      overVacancyLimit
    ) {
      return;
    }
    setError(null);
    const vacancyIds = Array.from(selectedVacancyIds);
    try {
      const result = await multiMutation.mutateAsync({ candidateIds, vacancyIds });

      const notice = isSingle
        ? singleAssignNotice(candidate!.fullName, result)
        : multiAssignNotice(result);
      toast[notice.tone](notice.text);

      onOpenChange(false);
      onSuccess?.();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to assign candidates. Try again.'));
    }
  };

  const title = isSingle
    ? `Assign ${candidate!.fullName}`
    : `Assign ${totalCandidates} candidate${totalCandidates === 1 ? '' : 's'} to vacancies`;

  const description = isSingle
    ? 'Choose one or more active vacancies. Each will get a pipeline entry in its first stage.'
    : 'Selected candidates will be added to the first stage of every chosen vacancy. Duplicates are skipped automatically.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          {/* pr-6 keeps a wrapped title clear of the dialog's absolutely-positioned close button */}
          <DialogTitle className="wrap-anywhere pr-6">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search active vacancies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>

          <div className="max-h-[320px] overflow-y-auto rounded-md border border-border">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
            ) : vacancies.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No active vacancies found.
              </div>
            ) : (
              <ul>
                {vacancies.map((v) => {
                  const isChecked = selectedVacancyIds.has(v.id);
                  const already = alreadyAssignedIds.has(v.id);
                  return (
                    <li key={v.id}>
                      <label
                        className={`flex w-full cursor-pointer items-start gap-3 px-3 py-2 border-b last:border-b-0 transition-colors ${
                          isChecked ? 'bg-primary/10' : 'hover:bg-muted/50'
                        }`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleVacancy(v.id)}
                          aria-label={`Select ${v.title}`}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-sm" title={v.title}>
                            {v.title}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            {/* the "already assigned" marker stays outside the clamp: it decides
                                whether picking this row does anything */}
                            <span
                              className="truncate"
                              title={`${v.department?.name ?? 'No department'}${v.location ? ` · ${v.location}` : ''}`}
                            >
                              {v.department?.name ?? 'No department'}
                              {v.location ? ` · ${v.location}` : ''}
                            </span>
                            {already ? <span className="shrink-0">· already assigned</span> : null}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {selectedVacancyIds.size > 0 && (
            <p className="text-xs text-muted-foreground">
              {selectedVacancyIds.size} vacanc{selectedVacancyIds.size === 1 ? 'y' : 'ies'} selected
            </p>
          )}

          {overCandidateLimit && (
            <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              Maximum {MAX_BULK_CANDIDATES} candidates per assignment. Deselect{' '}
              {totalCandidates - MAX_BULK_CANDIDATES} to continue.
            </div>
          )}

          {overVacancyLimit && (
            <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              Maximum {MAX_VACANCIES} vacancies per assignment. Deselect{' '}
              {selectedVacancyIds.size - MAX_VACANCIES} to continue.
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="accent"
            onClick={handleConfirm}
            disabled={
              selectedVacancyIds.size === 0 ||
              totalCandidates === 0 ||
              overCandidateLimit ||
              overVacancyLimit ||
              multiMutation.isPending
            }
          >
            {multiMutation.isPending ? 'Assigning...' : isSingle ? 'Assign' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
